import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    segment: 'all'
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Error al cargar las plantillas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/templates`, formData);
      toast.success('Plantilla creada exitosamente');
      setShowCreateDialog(false);
      setFormData({ name: '', subject: '', content: '', segment: 'all' });
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Error al crear la plantilla');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) return;
    
    try {
      await axios.delete(`${API}/templates/${id}`);
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error al eliminar la plantilla');
    }
  };

  const handleUsePreset = (preset) => {
    setFormData({
      name: preset.name,
      subject: preset.subject,
      content: preset.content,
      segment: preset.segment
    });
  };

  const presetTemplates = [
    {
      name: 'Promoción Hoteles - Empresas',
      segment: 'empresa',
      subject: '🏨 Oferta Especial para Empresas - Nuestros Hoteles',
      content: `Hola {{name}},

¡Tenemos una oferta especial para {{company}}!

Descubre nuestros 3 hoteles ideales para eventos empresariales:

🏨 Hotel Ciutat Martorell
- Ubicación céntrica en Martorell
- Salas de reuniones modernas
- Servicios corporativos completos

🏨 Port Sitges Hotel
- Vista al mar en Sitges
- Espacios para conferencias
- Ambiente relajante para team building

🏨 Hostal San Francisco
- Entorno acogedor
- Ideal para grupos pequeños
- Excelente relación calidad-precio

✨ Oferta Especial: 20% de descuento en reservas corporativas este mes

📧 Contáctanos para más información y disponibilidad

Saludos cordiales,
Equipo de Gestión Hotelera`
    },
    {
      name: 'Salas de Reuniones - Asociaciones',
      segment: 'asociacion',
      subject: '🤝 Espacios perfectos para reuniones de asociaciones',
      content: `Estimado/a {{name}},

Desde {{company}}, sabemos lo importante que son las reuniones para vuestra asociación.

Ofrecemos espacios ideales para vuestras reuniones y eventos:

✅ Salas equipadas con tecnología moderna
✅ Capacidad flexible: de 10 a 100 personas
✅ Servicio de catering disponible
✅ Ubicaciones estratégicas en Martorell y Sitges
✅ Tarifas especiales para asociaciones

🎯 Disponible en nuestros 3 establecimientos:
- Hotel Ciutat Martorell
- Port Sitges Hotel
- Hostal San Francisco

💼 Tarifas preferenciales para asociaciones
📅 Reserva flexible sin penalización

¿Hablamos de vuestras necesidades?

Un cordial saludo,
Equipo de Eventos`
    }
  ];

  const getSegmentLabel = (segment) => {
    const labels = {
      all: 'Todos',
      empresa: 'Empresas',
      asociacion: 'Asociaciones',
      particular: 'Particulares',
      agencia_de_viajes: 'Agencias de Viajes',
      colegio: 'Colegios',
      ninguno: 'Ninguno'
    };
    return labels[segment] || segment;
  };

  const getSegmentColor = (segment) => {
    const colors = {
      all: 'bg-blue-100 text-blue-700',
      empresa: 'bg-purple-100 text-purple-700',
      asociacion: 'bg-green-100 text-green-700',
      particular: 'bg-sky-100 text-sky-700',
      agencia_de_viajes: 'bg-orange-100 text-orange-700',
      colegio: 'bg-teal-100 text-teal-700',
      ninguno: 'bg-gray-100 text-gray-700'
    };
    return colors[segment] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plantillas de Email</h1>
          <p className="mt-2 text-gray-600">Crea plantillas reutilizables para tus campañas</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-template-button">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Plantilla
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-template-dialog">
            <DialogHeader>
              <DialogTitle>Crear Nueva Plantilla</DialogTitle>
              <DialogDescription>
                Crea una plantilla reutilizable. Usa {"{{name}}"} y {"{{company}}"} para personalizar.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div>
                <Label>Plantillas Predefinidas</Label>
                <div className="grid grid-cols-1 gap-2 mt-2">
                  {presetTemplates.map((preset, idx) => (
                    <Button
                      key={idx}
                      type="button"
                      variant="outline"
                      onClick={() => handleUsePreset(preset)}
                      className="justify-start"
                      data-testid={`preset-template-${idx}`}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {preset.name}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="template-name">Nombre de la Plantilla *</Label>
                <Input
                  id="template-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Promoción Primavera - Empresas"
                  required
                  data-testid="template-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="template-segment">Segmento *</Label>
                <Select value={formData.segment} onValueChange={(value) => setFormData({ ...formData, segment: value })}>
                  <SelectTrigger data-testid="template-segment-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="template-segment-all">Todos</SelectItem>
                    <SelectItem value="empresa" data-testid="template-segment-empresa">Empresas</SelectItem>
                    <SelectItem value="asociacion" data-testid="template-segment-asociacion">Asociaciones</SelectItem>
                    <SelectItem value="particular" data-testid="template-segment-particular">Particulares</SelectItem>
                    <SelectItem value="agencia_de_viajes" data-testid="template-segment-agencia">Agencias de Viajes</SelectItem>
                    <SelectItem value="colegio" data-testid="template-segment-colegio">Colegios</SelectItem>
                    <SelectItem value="ninguno" data-testid="template-segment-ninguno">Ninguno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="template-subject">Asunto del Email *</Label>
                <Input
                  id="template-subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Ej: Oferta especial en nuestros hoteles"
                  required
                  data-testid="template-subject-input"
                />
              </div>
              
              <div>
                <Label htmlFor="template-content">Contenido del Email *</Label>
                <Textarea
                  id="template-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={12}
                  placeholder="Contenido del email..."
                  required
                  className="font-mono text-sm"
                  data-testid="template-content-textarea"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Usa {"{{name}}"} para el nombre y {"{{company}}"} para la empresa del contacto
                </p>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" className="flex-1" data-testid="confirm-create-template-button">Crear Plantilla</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateDialog(false)}
                  data-testid="cancel-create-template-button"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Cargando plantillas...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.map((template) => (
            <Card key={template.id} data-testid={`template-card-${template.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg" data-testid={`template-name-${template.id}`}>{template.name}</CardTitle>
                    <CardDescription className="mt-1" data-testid={`template-subject-${template.id}`}>{template.subject}</CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteTemplate(template.id)}
                    data-testid={`delete-template-${template.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
                <Badge className={`${getSegmentColor(template.segment)} w-fit mt-2`} data-testid={`template-segment-${template.id}`}>
                  {getSegmentLabel(template.segment)}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-3 rounded border max-h-40 overflow-y-auto" data-testid={`template-content-${template.id}`}>
                  <pre className="text-sm whitespace-pre-wrap font-sans">{template.content}</pre>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {templates.length === 0 && (
            <Card className="col-span-full">
              <CardContent className="text-center py-8 text-gray-500">
                No hay plantillas creadas. Crea tu primera plantilla para comenzar.
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default Templates;